import React, { useState, useEffect, useRef, useReducer, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Avatar,
  Container,
  Grid,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  Divider,
  ListItemIcon,
  Popover,
  Menu,
  MenuItem,
  Chip,
  InputAdornment,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AiActions } from '../../../Services/_actions/index.js';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import MathRenderer from '../MathRenderer.tsx';
import { avatarOptions, AvatarOption } from './Avatarselector.tsx';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import Tooltip from '@mui/material/Tooltip';
import { useKnowledgeMapSubjects } from '../../Sidebar/ConceptBuilderSideBar.tsx';
import { useWebSocketAiContext } from '../../../utils/WebSocketContext.tsx';
import ConceptBuilderSidebar from './ConceptBuilderSidebar.tsx';
import TopicSubtopicCards from './TopicSubtopicCards.tsx';
import SchoolIcon from '@mui/icons-material/School';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MermaidDiagram from './MermaidDiagram.tsx';
import FloatingActionBar from './FloatingActionBar.tsx';
import ImagePreview from './ImagePreview.tsx';
import ResponseNotificationSnackbar from './ResponseNotificationSnackbar.tsx';
import AnimatedVideoPlayer from './AnimatedVideoPlayer.tsx';
import { VideoScene } from './AnimatedVideoPlayer.tsx';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { globalpermissions } from '../../../Services/Permission.js';
import { ConstantActions } from '../../../Services/_actions/constant.actions.js';
import CommonChatBubble from '../../FlowBuilder/Common/ChatMessage/Commonchatbubble.tsx';
import ConceptBuilderHelp from '../HelpDialog.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QuizIcon from '@mui/icons-material/Quiz';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TimelineIcon from '@mui/icons-material/Timeline';
import ActionMenu from './ActionMenu.tsx';

// ─── Types ──────────────────────────────────────────────────────────────────────
interface Message {
  id: number;
  type: 'user' | 'ai';
  content: string;
  diagram_mermaid?: string;
  diagram_title?: string;
  test_url?: string;
  difficulty_buttons?: string[];
  concept_questions?: string[];
  created_at?: string;
  image_url?: string | string[];
  video_scenes?: VideoScene[];
  video_title?: string;
}

interface SelectionState {
  subject: string;
  topic: string;
  subtopic: string;
  expandedTopic: string | null;
}

type SelectionAction =
  | { type: 'SET_SUBJECT'; subject: string }
  | { type: 'SET_TOPIC'; topic: string }
  | { type: 'SET_SUBTOPIC'; subtopic: string }
  | { type: 'SET_EXPANDED_TOPIC'; topic: string | null }
  | { type: 'CLEAR_TOPIC' }
  | { type: 'CLEAR_SUBTOPIC' }
  | { type: 'RESET' };

// ─── Reducers ──────────────────────────────────────────────────────────────────
const initialSelection: SelectionState = {
  subject: '',
  topic: '',
  subtopic: '',
  expandedTopic: null,
};

function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'SET_SUBJECT':
      return { subject: action.subject, topic: '', subtopic: '', expandedTopic: null };
    case 'SET_TOPIC':
      return { ...state, topic: action.topic, subtopic: '', expandedTopic: action.topic };
    case 'SET_SUBTOPIC':
      return { ...state, subtopic: action.subtopic };
    case 'SET_EXPANDED_TOPIC':
      return { ...state, expandedTopic: action.topic };
    case 'CLEAR_TOPIC':
      return { ...state, topic: '', subtopic: '', expandedTopic: null };
    case 'CLEAR_SUBTOPIC':
      return { ...state, subtopic: '' };
    case 'RESET':
      return initialSelection;
    default:
      return state;
  }
}

interface ChatState {
  messages: Message[];
  conversationId: string | null;
  activeConversationId: string | null;
  lastQuestion: string;
  lastResponse: string;
  showActionButtons: boolean;
  isWaitingForResponse: boolean;
  messageHistory: Message[][];
}

type ChatAction =
  | { type: 'ADD_MESSAGE'; message: Message }
  | { type: 'SET_MESSAGES'; messages: Message[] }
  | { type: 'REMOVE_LAST_MESSAGE' }
  | { type: 'SET_CONVERSATION_ID'; id: string | null }
  | { type: 'SET_ACTIVE_CONVERSATION_ID'; id: string | null }
  | { type: 'SET_LAST_QUESTION'; question: string }
  | { type: 'SET_LAST_RESPONSE'; response: string }
  | { type: 'SET_SHOW_ACTION_BUTTONS'; show: boolean }
  | { type: 'SET_WAITING'; waiting: boolean }
  | { type: 'PUSH_HISTORY'; messages: Message[] }
  | { type: 'RESET' };

const initialChatState: ChatState = {
  messages: [],
  conversationId: null,
  activeConversationId: null,
  lastQuestion: '',
  lastResponse: '',
  showActionButtons: false,
  isWaitingForResponse: false,
  messageHistory: [],
};

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] };
    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };
    case 'REMOVE_LAST_MESSAGE':
      return { ...state, messages: state.messages.slice(0, -1) };
    case 'SET_CONVERSATION_ID':
      return { ...state, conversationId: action.id };
    case 'SET_ACTIVE_CONVERSATION_ID':
      return { ...state, activeConversationId: action.id };
    case 'SET_LAST_QUESTION':
      return { ...state, lastQuestion: action.question };
    case 'SET_LAST_RESPONSE':
      return { ...state, lastResponse: action.response };
    case 'SET_SHOW_ACTION_BUTTONS':
      return { ...state, showActionButtons: action.show };
    case 'SET_WAITING':
      return { ...state, isWaitingForResponse: action.waiting };
    case 'PUSH_HISTORY':
      return { ...state, messageHistory: [...state.messageHistory, action.messages] };
    case 'RESET':
      return initialChatState;
    default:
      return state;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const extractAIText = (aiResp: any): string => {
  if (!aiResp) return 'No answer found';
  if (typeof aiResp === 'string') return aiResp.trim() || 'No answer found';
  if (aiResp?.data?.response?.agent_response) return aiResp.data.response.agent_response;
  const text =
    aiResp?.data?.response?.response ??
    aiResp?.response?.response?.response ??
    aiResp?.response?.response ??
    aiResp?.response ??
    aiResp?.output_query ??
    '';
  return typeof text === 'string' && text.trim().length > 0 ? text : 'No answer found';
};

const sanitizeImageUrl = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  return trimmed;
};

const getValidImageUrls = (imageUrl?: unknown): string[] => {
  if (Array.isArray(imageUrl)) {
    return imageUrl.map(sanitizeImageUrl).filter((u): u is string => Boolean(u));
  }
  const single = sanitizeImageUrl(imageUrl);
  return single ? [single] : [];
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
  const today = new Date(new Date().getTime() + 5.5 * 60 * 60 * 1000);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const strip = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (strip(istDate) === strip(today)) return 'Today';
  if (strip(istDate) === strip(yesterday)) return 'Yesterday';
  return istDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatTime = (dateString: string, addIst = false): string => {
  const date = new Date(dateString);
  const d = addIst ? new Date(date.getTime() + 5.5 * 60 * 60 * 1000) : date;
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const makeMessage = (
  type: 'user' | 'ai',
  content: string,
  extras: Partial<Message> = {}
): Message => ({
  id: Date.now() + Math.random(),
  type,
  content,
  ...extras,
});

const extractVideoFromResponse = (
  aiResp: any
): { videoScenes: VideoScene[] | null; videoTitle: string } => {
  const video = aiResp?.response?.video ?? aiResp?.data?.response?.video ?? null;
  if (!video) return { videoScenes: null, videoTitle: '' };
  const scenes: VideoScene[] = Array.isArray(video.scenes) ? video.scenes : [];
  const videoTitle: string = video.video_title ?? video.title ?? '';
  return { videoScenes: scenes.length > 0 ? scenes : null, videoTitle };
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DoubtAssistantPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState('');
  const { conversationId: urlConversationId, phoneNumber: urlPhoneNumber } = useParams<{
    conversationId?: string;
    phoneNumber?: string;
  }>();
  const { showSnackBar } = useSnackBar();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const actions = AiActions();
  const constantActions = ConstantActions();

  // ── State ──
  const [selection, dispatchSelection] = useReducer(selectionReducer, initialSelection);
  const { subject: selectedSubject, topic: selectedTopic, subtopic: selectedSubtopic, expandedTopic } = selection;

  const [chat, dispatchChat] = useReducer(chatReducer, initialChatState);
  const { messages, conversationId, activeConversationId, lastQuestion, lastResponse, showActionButtons, isWaitingForResponse } = chat;

  const [instructionSet, setInstructionSet] = useState('');
  const [userInput, setUserInput] = useState('');
  const [lastQuestionLocal, setLastQuestionLocal] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [leadFormId, setLeadFormId] = useState('');
  const [testCampaignId, setTestCampaignId] = useState('');
  const [testLeadFormId, setTestLeadFormId] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [currentFlowType, setCurrentFlowType] = useState<'doubt' | 'test' | null>(null);
  const [pendingResponseConversationId, setPendingResponseConversationId] = useState<string | null>(null);
  const [showResponseNotification, setShowResponseNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const loggedInUser = useRecoilValue(userInformationAtom);
  const mobile = loggedInUser?.mobile;
  const userId = loggedInUser?.id || loggedInUser?.user_id || '';
  const loggedInUsername = loggedInUser?.username || '';
  const [studentClass, setStudentClass] = useState('');
  const [classGroup, setClassGroup] = useState('');
  const [userAvatar, setUserAvatar] = useState<AvatarOption>(avatarOptions[0]);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const textFieldRef = useRef<HTMLTextAreaElement | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [sidebarTabValue, setSidebarTabValue] = useState(0);
  const [questionBank, setQuestionBank] = useState<any[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<any[]>([]);
  const [instructionMode, setInstructionMode] = useState<'default' | 'solution' | 'reasoning' | 'example' | 'diagram' | 'video'>('default');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [modeAnchorEl, setModeAnchorEl] = useState<null | HTMLElement>(null);
  const [awaitingDifficultySelection, setAwaitingDifficultySelection] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [queryHistory, setQueryHistory] = useState('');
  const hasConversationId = Boolean(urlConversationId);
  const isSelectionRequired = !hasConversationId;
  const isSuperuser = loggedInUser?.is_superuser === true || loggedInUser?.profile?.name === 'Admin';
  const [superuserSelectedClass, setSuperuserSelectedClass] = useState('');
  const hasClassListPermission = isSuperuser || constantActions.userHasPermission([globalpermissions?.performancePermissions?.classListAccess]);
  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  const [showSubjectSelector, setShowSubjectSelector] = useState(!selectedSubject && !messages.length);

  const {
    subjects,
    loading: subjectsLoading,
    subjectHierarchy,
    availableSubjectsFromClass,
    knowledgeMapData,
    entityData: data,
  } = useKnowledgeMapSubjects(true);
  const { state } = useWebSocketAiContext();

  const subjectFromUrl = searchParams.get('subject') || '';
  const topicFromUrl = searchParams.get('topic') || '';
  const subtopicFromUrl = searchParams.get('subtopic') || '';
  const previousSubjectFromUrlRef = useRef('');
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const { organisation_id, board, subject, topic, subtopic, subtopicData, class: classFromState } = location.state || {};

  // ─── Auto-fill selection from location.state ──────────────────────────────
  useEffect(() => {
    if (!location.state) return;
    const { organisation_id, class: classFromState, board, subject, topic, subtopic } = location.state;
    if (organisation_id) setSelectedOrganisation({ organisation_id });
    if (classFromState) {
      setSuperuserSelectedClass(String(classFromState));
      setStudentClass(String(classFromState));
    }
    if (board) setSelectedBoard(board);
    if (subject) dispatchSelection({ type: 'SET_SUBJECT', subject });
    if (topic) dispatchSelection({ type: 'SET_TOPIC', topic });
    if (subtopic) dispatchSelection({ type: 'SET_SUBTOPIC', subtopic });
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state]);

  // ─── Knowledge Map Index ──────────────────────────────────────────────────
  const knowledgeMapIndex = useMemo(() => {
    const index = new Map<string, any>();
    if (!knowledgeMapData || !studentClass) return index;
    const classNum = Number(studentClass);
    const rangeMatches = new Map<string, any>();
    for (const item of knowledgeMapData) {
      if (!item.subject || !item.class) continue;
      if (item.class.includes('-')) {
        const [start, end] = item.class.split('-').map((v: string) => Number(v.trim()));
        if (classNum >= start && classNum <= end && !rangeMatches.has(item.subject)) {
          rangeMatches.set(item.subject, item);
        }
      } else {
        if (Number(item.class) === classNum) index.set(item.subject, item);
      }
    }
    rangeMatches.forEach((item, subject) => {
      if (!index.has(subject)) index.set(subject, item);
    });
    return index;
  }, [knowledgeMapData, studentClass]);

  const getKnowledgeEntry = useCallback(
    (subject: string) => knowledgeMapIndex.get(subject) ?? null,
    [knowledgeMapIndex]
  );

  // ─── Derived data ──────────────────────────────────────────────────────────
  const boardOptions = useMemo(() => {
    if (!knowledgeMapData) return [];
    const orgFilteredData = selectedOrganisation?.organisation_id
      ? knowledgeMapData.filter((item: any) => item.organisation_id === selectedOrganisation.organisation_id)
      : knowledgeMapData;
    return [...new Set(orgFilteredData.map((item: any) => item.board).filter(Boolean))]
      .sort()
      .map((b) => ({ label: b, value: b }));
  }, [knowledgeMapData, selectedOrganisation]);

  const getInstructionSet = useCallback(
    (subject: string, type: 'doubt' | 'test' = 'doubt') => {
      const entry = getKnowledgeEntry(subject);
      if (!entry) return '';
      return type === 'test' ? entry.test_instruction_set ?? '' : entry.instruction_set ?? '';
    },
    [getKnowledgeEntry]
  );

  const getSolutionInstruction = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.step_by_step_solution_instruction ?? '',
    [getKnowledgeEntry]
  );

  const getReasoningInstruction = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.step_by_step_reasoning_instruction ?? '',
    [getKnowledgeEntry]
  );

  const getDiagramInstructionText = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.diagram_instruction ?? '',
    [getKnowledgeEntry]
  );

  const getVideoInstruction = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.video_instruction ?? '',
    [getKnowledgeEntry]
  );

  const activeInstructionSet = useMemo(() => {
    if (!selectedSubject) return '';
    switch (instructionMode) {
      case 'solution': return getSolutionInstruction(selectedSubject);
      // case 'reasoning': return getReasoningInstruction(selectedSubject);
      case 'reasoning':
        return [
          getSolutionInstruction(selectedSubject),
          getReasoningInstruction(selectedSubject),
        ].filter(Boolean).join('\n');
      case 'diagram': return getDiagramInstructionText(selectedSubject);
      case 'video': return getVideoInstruction(selectedSubject);
      default: return getInstructionSet(selectedSubject, 'doubt');
    }
  }, [selectedSubject, instructionMode, getSolutionInstruction, getReasoningInstruction, getDiagramInstructionText, getInstructionSet]);

  // ─── Campaign IDs ──────────────────────────────────────────────────────────
  useEffect(() => {
    const entry = getKnowledgeEntry(selectedSubject);
    if (!entry) {
      setCampaignId('');
      setLeadFormId('');
      setTestCampaignId('');
      setTestLeadFormId('');
      return;
    }
    setCampaignId(entry.campaign_id ?? '');
    setLeadFormId(entry.lead_form_id ?? '');
    setTestCampaignId(entry.test_campaign_id ?? '');
    setTestLeadFormId(entry.test_lead_form_id ?? '');
  }, [selectedSubject, getKnowledgeEntry]);

  // ─── Image URLs ──────────────────────────────────────────────────────────
  useEffect(() => {
    const urls = imageFiles.map((f) => URL.createObjectURL(f));
    setImagePreviewUrls(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [imageFiles]);

  // ─── Available classes ────────────────────────────────────────────────────
  const availableClasses = useMemo(() => {
    if (!knowledgeMapData || !hasClassListPermission) return [];
    const orgFilteredData = selectedOrganisation?.organisation_id
      ? knowledgeMapData.filter((item: any) => item.organisation_id === selectedOrganisation.organisation_id)
      : knowledgeMapData;
    const classSet = new Set<string>();
    orgFilteredData.forEach((item: any) => {
      if (!item.class) return;
      const cls = String(item.class).trim();
      const normalized = cls.split('-').map((v) => String(Number(v.trim()))).join('-');
      classSet.add(normalized);
    });
    return Array.from(classSet).sort((a, b) => {
      const aIsRange = a.includes('-');
      const bIsRange = b.includes('-');
      if (!aIsRange && !bIsRange) return Number(a) - Number(b);
      if (!aIsRange && bIsRange) return -1;
      if (aIsRange && !bIsRange) return 1;
      return a.localeCompare(b);
    });
  }, [knowledgeMapData, hasClassListPermission, selectedOrganisation]);

  const subjectOptions = useMemo(() => {
    if (hasClassListPermission) {
      if (!superuserSelectedClass) return [];
      const normalizeClass = (cls: string) =>
        cls.split('-').map((v) => String(Number(v.trim()))).join('-');
      const selectedNormalized = normalizeClass(superuserSelectedClass);
      const validSubjects = new Set<string>();
      const orgFilteredData = selectedOrganisation?.organisation_id
        ? knowledgeMapData?.filter((item: any) => item.organisation_id === selectedOrganisation.organisation_id)
        : knowledgeMapData;
      orgFilteredData?.forEach((item: any) => {
        if (!item.subject || !item.class) return;
        const itemNormalized = normalizeClass(String(item.class).trim());
        if (itemNormalized === selectedNormalized) {
          validSubjects.add(item.subject);
          return;
        }
        if (itemNormalized.includes('-')) {
          const [start, end] = itemNormalized.split('-').map(Number);
          const selectedNum = Number(superuserSelectedClass);
          if (!isNaN(selectedNum) && selectedNum >= start && selectedNum <= end) {
            validSubjects.add(item.subject);
          }
        }
      });
      return Array.from(validSubjects).map((s) => ({ label: s, value: s }));
    }
    return subjects
      .filter((s) => availableSubjectsFromClass.includes(s.value))
      .map((s) => ({ label: s.label, value: s.value }));
  }, [subjects, availableSubjectsFromClass, hasClassListPermission, superuserSelectedClass, knowledgeMapData, selectedOrganisation]);

  const findKnowledgeEntry = useCallback(
    (subject: string) => {
      if (!knowledgeMapData || !superuserSelectedClass) return null;
      const normalizeClass = (cls: string) =>
        cls.split('-').map((v) => String(Number(v.trim()))).join('-');
      const selectedNormalized = normalizeClass(superuserSelectedClass);
      const orgFilteredData = selectedOrganisation?.organisation_id
        ? knowledgeMapData.filter((item: any) => item.organisation_id === selectedOrganisation.organisation_id)
        : knowledgeMapData;
      return orgFilteredData.find((item: any) => {
        if (!item.subject || !item.class) return false;
        if (item.subject !== subject) return false;
        const itemNormalized = normalizeClass(String(item.class).trim());
        if (itemNormalized === selectedNormalized) return true;
        if (itemNormalized.includes('-')) {
          const [start, end] = itemNormalized.split('-').map(Number);
          const num = Number(superuserSelectedClass);
          return !isNaN(num) && num >= start && num <= end;
        }
        return false;
      }) ?? null;
    },
    [knowledgeMapData, superuserSelectedClass, selectedOrganisation]
  );

  const availableTopics = useMemo(() => {
    if (hasClassListPermission && superuserSelectedClass && selectedSubject) {
      const entry = findKnowledgeEntry(selectedSubject);
      return (entry?.topics || []).map((t: any) => t.topic).filter(Boolean);
    }
    return selectedSubject ? Object.keys(subjectHierarchy?.[selectedSubject] || {}) : [];
  }, [hasClassListPermission, superuserSelectedClass, selectedSubject, findKnowledgeEntry, subjectHierarchy]);

  const availableSubtopics = useMemo(() => {
    if (hasClassListPermission && superuserSelectedClass && selectedSubject && selectedTopic) {
      const entry = findKnowledgeEntry(selectedSubject);
      const topicEntry = entry?.topics?.find((t: any) => t.topic === selectedTopic);
      return (topicEntry?.subtopics || []).map((s: any) => s.subtopic).filter(Boolean);
    }
    return selectedSubject && selectedTopic
      ? subjectHierarchy?.[selectedSubject]?.[selectedTopic] || []
      : [];
  }, [hasClassListPermission, superuserSelectedClass, selectedSubject, selectedTopic, findKnowledgeEntry, subjectHierarchy]);

  const topicOptions = useMemo(
    () => availableTopics.map((t) => ({ label: t, value: t })),
    [availableTopics]
  );

  const subtopicOptions = useMemo(
    () => availableSubtopics?.map((s) => ({ label: s, value: s })),
    [availableSubtopics]
  );

  const sortedTopics = useMemo(
    () => (selectedTopic ? [selectedTopic, ...availableTopics.filter((t) => t !== selectedTopic)] : availableTopics),
    [selectedTopic, availableTopics]
  );

  const filteredQuestionsComputed = useMemo(() => {
    if (!selectedSubject || !selectedTopic || !selectedSubtopic) return [];
    const filtered = questionBank.filter(
      (item) =>
        item.entity.subject === selectedSubject &&
        item.entity.topic === selectedTopic &&
        item.entity.sub_topic === selectedSubtopic
    );
    return [...filtered].sort(() => Math.random() - 0.5).slice(0, 3);
  }, [selectedSubject, selectedTopic, selectedSubtopic, questionBank]);

  // ─── Add message ──────────────────────────────────────────────────────────
  const addMessage = useCallback((type: 'user' | 'ai', content: string, extras: Partial<Message> = {}) => {
    dispatchChat({ type: 'ADD_MESSAGE', message: makeMessage(type, content, extras) });
  }, []);

  const resetChatState = useCallback(() => {
    dispatchChat({ type: 'RESET' });
    setUserInput('');
  }, []);

  useEffect(() => {
    if (subjectFromUrl) {
      dispatchSelection({ type: 'SET_SUBJECT', subject: subjectFromUrl });
      if (topicFromUrl) dispatchSelection({ type: 'SET_TOPIC', topic: topicFromUrl });
      if (subtopicFromUrl) dispatchSelection({ type: 'SET_SUBTOPIC', subtopic: subtopicFromUrl });
      setShowSubjectSelector(false);
    }
  }, [searchParams]);

  useEffect(() => {
    const prev = previousSubjectFromUrlRef.current;
    if (prev !== subjectFromUrl) {
      if (prev || subjectFromUrl) resetChatState();
      previousSubjectFromUrlRef.current = subjectFromUrl;
    }
  }, [subjectFromUrl, resetChatState]);

  useEffect(() => {
    if (data && loggedInUsername) handleClassGroupFromData(data);
  }, [data, loggedInUsername]);

  useEffect(() => {
    const saved = localStorage.getItem('userAvatar');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      const found = avatarOptions.find((a) => a.id === parsed.id);
      if (found) setUserAvatar(found);
    } catch { /* ignore */ }
  }, []);

  const handleClassGroupFromData = (entityData: any) => {
    if (!entityData?.items?.length || !loggedInUsername) return;
    entityData.items.forEach((item: any) => {
      if (item?.entity?.username?.toLowerCase() === loggedInUsername.toLowerCase()) {
        setStudentClass(item.entity.class);
        setClassGroup(item.entity.class_group);
      }
    });
  };

  const fetchQuestionBank = useCallback(async () => {
    try {
      const response = await actions.getRecordEntity('', { template_form_type: 'question_bank_form' });
      if (response?.items) setQuestionBank(response.items);
    } catch (e) {
      console.error('Error fetching question bank:', e);
    }
  }, []);

  useEffect(() => {
    fetchQuestionBank();
  }, [fetchQuestionBank]);

  const fetchConversationList = useCallback(
    async (forceNoFilter = false) => {
      try {
        const payload = {
          user_id: userId,
          phone_number: mobile ? `91${mobile}` : '',
          parameters: {
            ...(!forceNoFilter && sidebarTabValue === 1 && selectedSubject ? { subject: selectedSubject } : {}),
            ...(!forceNoFilter && sidebarTabValue === 1 && selectedTopic ? { topic: selectedTopic } : {}),
            dashboard: 'concept-builder',
          },
        };
        const response = await actions.getConversationList(payload);
        if (response?.status && Array.isArray(response.data)) {
          const list = response.data.map((c: any) => ({ ...c, last_query: c.last_query || c.query || 'No query' }));
          setConversations(list);
          applyRouteConversationContext(list, urlConversationId);
        }
      } catch (e) {
        console.error('Failed to fetch conversations:', e);
      }
    },
    [userId, mobile, selectedSubject, selectedTopic, sidebarTabValue, urlConversationId]
  );

  useEffect(() => {
    if (userId) fetchConversationList(sidebarTabValue === 0);
  }, [selectedSubject, selectedTopic, userId, sidebarTabValue]);

  const applyRouteConversationContext = (list: any[], routeConversationId?: string) => {
    if (!routeConversationId || !Array.isArray(list)) return;
    const matched = list.find((c) => c.conversation_id === routeConversationId);
    if (!matched?.parameters) return;
    const { subject = '', topic = '', subtopic = '' } = matched.parameters;
    if (subject) dispatchSelection({ type: 'SET_SUBJECT', subject });
    if (topic) dispatchSelection({ type: 'SET_TOPIC', topic });
    if (subtopic) dispatchSelection({ type: 'SET_SUBTOPIC', subtopic });
    setShowSubjectSelector(false);
  };

  const fetchQuery = useCallback(async (cid: string) => {
    try {
      const response = await actions.getChatHistory({ conversation_id: cid, ai_model: 'mdai' });
      if (!response?.status || !Array.isArray(response.data)) return;

      const chatMessages: Message[] = response.data.flatMap((item: any) => {
        const msgs: Message[] = [];
        if (item.query) {
          msgs.push(
            makeMessage('user', item.query, {
              created_at: item.created_at,
              image_url: item?.image_urls || '',
            })
          );
        }
        const aiText = extractAIText(item?.response);
        const conceptQuestions = item?.response?.response?.concept_check_questions;
        const imageUrl = item?.response?.data?.response?.image_url || '';
        const diagramMermaid = item?.response?.response?.diagrams?.mermaid_code || '';
        const diagramTitle = item?.response?.response?.diagrams?.diagram_title || '';
        const { videoScenes: vs, videoTitle: vt } = extractVideoFromResponse(item?.response);
        msgs.push(
          makeMessage('ai', aiText, {
            diagram_mermaid: diagramMermaid,
            diagram_title: diagramTitle,
            created_at: item.created_at,
            ...(imageUrl && { image_url: imageUrl }),
            ...(Array.isArray(conceptQuestions) && conceptQuestions.length > 0
              ? { concept_questions: conceptQuestions }
              : {}),
            ...(vs ? { video_scenes: vs, video_title: vt } : {}),
          })
        );
        return msgs;
      });

      dispatchChat({ type: 'SET_MESSAGES', messages: chatMessages });
      dispatchChat({ type: 'SET_CONVERSATION_ID', id: cid });
      dispatchChat({ type: 'SET_ACTIVE_CONVERSATION_ID', id: cid });
      dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });

      const lastItem = response.data[response.data.length - 1];
      if (lastItem) dispatchChat({ type: 'SET_LAST_QUESTION', question: lastItem.query || '' });

      const apiParams = response.data[0]?.parameters;
      const responseData = response.data[0]?.response?.response;
      const subject = apiParams?.subject || responseData?.subject || '';
      const topic = apiParams?.topic || responseData?.topic || '';
      const subtopic = apiParams?.subtopic || responseData?.subtopic || '';
      const queryHistory = lastItem?.query || apiParams?.query || lastItem?.response?.response?.query || '';

      setQueryHistory(queryHistory);
      if (subject) dispatchSelection({ type: 'SET_SUBJECT', subject });
      if (topic) dispatchSelection({ type: 'SET_TOPIC', topic });
      if (subtopic) dispatchSelection({ type: 'SET_SUBTOPIC', subtopic });
      setShowSubjectSelector(false);
      if (isMobile) setSidebarOpen(false);
    } catch (e) {
      console.error('Failed to fetch conversation:', e);
    }
  }, [isMobile]);

  // ─── Shared conversation from URL ────────────────────────────────────────
  useEffect(() => {
    if (!urlConversationId || !urlPhoneNumber) return;
    const isUserId = urlPhoneNumber.length < 12;
    if (isUserId) {
      if (String(urlPhoneNumber) !== String(userId)) {
        showSnackBar('This conversation belongs to a different user.', 'error');
        navigate('/r/student/concept-builder');
        return;
      }
    } else {
      const clean = (p: string) => (p.startsWith('91') ? p.substring(2) : p);
      if (clean(urlPhoneNumber) !== clean(mobile || '')) {
        showSnackBar('This conversation belongs to a different user.', 'error');
        navigate('/r/student/concept-builder');
        return;
      }
    }
    fetchQuery(urlConversationId)
      .then(() => showSnackBar('Conversation loaded successfully!', 'success'))
      .catch(() => {
        showSnackBar('Failed to load the conversation.', 'error');
        navigate('/r/student/concept-builder');
      });
  }, [urlConversationId, urlPhoneNumber]);

  // ─── Scroll to bottom ──────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── WebSocket response handler ──────────────────────────────────────────
  useEffect(() => {
    if (!state?.imageGeneration) return;
    const imageUrl = state.imageGeneration.image_url;
    if (!imageUrl) return;
    dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
    dispatchChat({
      type: 'ADD_MESSAGE',
      message: makeMessage('ai', '🖼️ Here is your generated image:', { image_url: imageUrl }),
    });
  }, [state?.imageGeneration]);

  useEffect(() => {
    if (!state?.aiRes) return;
    const aiResp = state.aiRes.ai_response;
    const responseConversationId = state.aiRes.conversation_id;
    const isDifferentChat =
      responseConversationId &&
      activeConversationId &&
      responseConversationId !== activeConversationId;

    if (isDifferentChat) {
      const aiText =
        typeof aiResp === 'string'
          ? aiResp
          : aiResp?.response?.response || aiResp?.output_query || 'Response received';
      setNotificationMessage(aiText);
      setShowResponseNotification(true);
      setPendingResponseConversationId(responseConversationId);
      dispatchChat({ type: 'SET_WAITING', waiting: false });
      return;
    }

    dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });

    if (currentFlowType === 'test') {
      const testUrl = aiResp?.response?.test_url;
      const content = aiResp?.response?.response || '📝 Test Paper Generated!';
      dispatchChat({
        type: 'ADD_MESSAGE',
        message: makeMessage('ai', content, testUrl ? { test_url: testUrl } : {}),
      });
    } else {
      const aiText = typeof aiResp === 'string'
        ? aiResp
        : aiResp?.response?.response || aiResp?.output_query || 'No answer found';
      const conceptQuestions =
        aiResp?.response?.response?.concept_check_questions ||
        aiResp?.response?.concept_check_questions;
      const diagramMermaid = aiResp?.response?.diagrams?.mermaid_code || '';
      const diagramTitle = aiResp?.response?.diagrams?.diagram_title || '';
      const { videoScenes, videoTitle } = extractVideoFromResponse(aiResp);

      dispatchChat({ type: 'SET_LAST_RESPONSE', response: String(aiText) });
      dispatchChat({
        type: 'ADD_MESSAGE',
        message: makeMessage('ai', String(aiText), {
          diagram_mermaid: diagramMermaid,
          diagram_title: diagramTitle,
          ...(Array.isArray(conceptQuestions) && conceptQuestions.length > 0
            ? { concept_questions: conceptQuestions }
            : {}),
          ...(videoScenes ? { video_scenes: videoScenes, video_title: videoTitle } : {}),
        }),
      });
    }

    if (responseConversationId) {
      dispatchChat({ type: 'SET_CONVERSATION_ID', id: responseConversationId });
      dispatchChat({ type: 'SET_ACTIVE_CONVERSATION_ID', id: responseConversationId });
      fetchConversationList();
      setConversations((prev) => {
        const exists = prev.some((c) => c.conversation_id === responseConversationId);
        if (exists)
          return prev.map((c) =>
            c.conversation_id === responseConversationId
              ? { ...c, query: lastQuestion, last_query: lastQuestion }
              : c
          );
        return [
          { conversation_id: responseConversationId, query: lastQuestion, last_query: lastQuestion },
          ...prev,
        ];
      });
    }

    dispatchChat({ type: 'SET_WAITING', waiting: false });
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });
    setCurrentFlowType(null);
    setAwaitingDifficultySelection(false);
    setPendingResponseConversationId(null);
  }, [state?.aiRes]);

  const handleApiCall = useCallback(async (
    type: 'doubt' | 'test',
    difficulty?: 'easy' | 'medium' | 'hard',
    bypassSelectionCheck = false
  ) => {
    if (type === 'doubt' && !userInput.trim() && imageFiles.length === 0) return;
    if (!bypassSelectionCheck && isSelectionRequired && (!selectedSubject)) {
      showSnackBar('Please select Subject before asking a question.', 'warning');
      return;
    }

    dispatchChat({ type: 'PUSH_HISTORY', messages });
    setCurrentFlowType(type);
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    dispatchChat({ type: 'SET_WAITING', waiting: true });

    const queryConversationId = conversationId;
    setPendingResponseConversationId(queryConversationId);

    if (type === 'doubt') {
      if (imageFiles.length > 0) {
        dispatchChat({
          type: 'ADD_MESSAGE',
          message: makeMessage('user', userInput || '', { image_url: imagePreviewUrls }),
        });
      } else {
        addMessage('user', userInput);
      }
      dispatchChat({ type: 'SET_LAST_QUESTION', question: userInput });
      setLastQuestionLocal(userInput);
      setUserInput('');
      addMessage('ai', `Let me think for a moment… 🤔\nI'm carefully preparing something special for you.\nThis may take up to a minute.`);
    } else {
      addMessage('user', `Selected: ${difficulty?.charAt(0).toUpperCase()}${difficulty?.slice(1)}`);
      addMessage('ai', 'Generating test paper... 📝');
    }

    try {
      const instructionParts = [
        `You are teaching a student of Class ${studentClass}.`,
        `Answer strictly according to this class level.`,
        ...(selectedSubject ? [`Subject: ${selectedSubject}.`] : []),
        ...(selectedTopic ? [`Topic: ${selectedTopic}.`] : []),
        ...(selectedSubtopic ? [`Subtopic: ${selectedSubtopic}.`] : []),
      ];

      if (type === 'doubt') {
        if (instructionMode === 'solution') instructionParts.push('Provide a detailed step-by-step solution.');
        // else if (instructionMode === 'reasoning') instructionParts.push('Explain the step-by-step reasoning process.');
        else if (instructionMode === 'reasoning') {
          instructionParts.push('Provide a detailed step-by-step solution.');
          instructionParts.push('Explain the step-by-step reasoning process.');
        }
        else if (instructionMode === 'diagram') {
          instructionParts.push('Explain using a diagram or visual representation.');
          instructionParts.push('Explain the step-by-step reasoning process.');
        }
        instructionParts.push(activeInstructionSet);
      } else {
        instructionParts.push(getInstructionSet(selectedSubject, 'test'));
      }

      const buildFormData = (overrides: Record<string, string> = {}) => {
        const fd = new FormData();
        fd.append('flowType', type);
        fd.append('class', studentClass);
        fd.append('parameters', JSON.stringify({
          subject: selectedSubject,
          topic: selectedTopic,
          subtopic: selectedSubtopic,
          board: selectedBoard,
          dashboard: 'concept-builder'
        }));
        fd.append('instruction', instructionParts.join('\n'));
        if (queryConversationId) fd.append('conversation_id', queryConversationId);
        Object.entries(overrides).forEach(([k, v]) => fd.append(k, v));
        return fd;
      };

      if (type === 'doubt') {
        const fd = buildFormData({
          query: userInput || '',
          campaign_id: campaignId || '',
          lead_form_id: leadFormId || '',
        });
        imageFiles.forEach((file) => fd.append('images', file));
        setImageFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        await actions.sendDoubtWithImages(fd);
      } else {
        const query = `${lastQuestionLocal ? `Generate test for "${lastQuestionLocal}"` : 'Generate test paper'} ${selectedSubject} ${selectedTopic} ${selectedSubtopic || ''} test level ${difficulty} for class ${studentClass}`;
        const fd = buildFormData({
          query,
          campaign_id: testCampaignId || '',
          lead_form_id: testLeadFormId || '',
          level: difficulty || '',
        });
        fd.delete('parameters');
        fd.append('parameters', JSON.stringify({
          subject: selectedSubject,
          topic: selectedTopic,
          subtopic: selectedSubtopic,
          board: selectedBoard,
          difficulty
        }));
        await actions.sendDoubtWithImages(fd);
      }
    } catch (e) {
      dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
      addMessage('ai', '❌ Sorry, something went wrong. Please try again.');
      dispatchChat({ type: 'SET_WAITING', waiting: false });
      dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });
      setAwaitingDifficultySelection(false);
      setPendingResponseConversationId(null);
      console.error(e);
    }
  }, [userInput, imageFiles, imagePreviewUrls, isSelectionRequired, selectedSubject, selectedTopic, selectedSubtopic, messages, conversationId, studentClass, instructionMode, activeInstructionSet, campaignId, leadFormId, testCampaignId, testLeadFormId, lastQuestionLocal, getInstructionSet, addMessage]);

  // ─── Image upload ──────────────────────────────────────────────────────────
  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImageFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClearImage = useCallback(() => {
    setImageFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleRemoveImage = useCallback((index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ─── Speech recognition ────────────────────────────────────────────────────
  const stopMic = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const handleMicClick = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }
    if (isListening) { stopMic(); return; }
    setUserInput('');
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    recognitionRef.current = rec;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (event: SpeechRecognitionEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) setUserInput((p) => p + event.results[i][0].transcript + ' ');
      }
    };
    rec.onend = () => setIsListening(false);
    rec.onerror = () => setIsListening(false);
    rec.start();
    setIsListening(true);
  }, [isListening, stopMic]);

  // ─── Navigation handlers ──────────────────────────────────────────────────
  const handleSubjectChange = useCallback((subject: string) => {
    dispatchSelection({ type: 'SET_SUBJECT', subject });
    resetChatState();
    setShowSubjectSelector(false);
    navigate(`/r/student/concept-builder?subject=${subject}`, { replace: true });
  }, [navigate, resetChatState]);

  const handleTopicChange = useCallback((topic: string) => {
    dispatchSelection({ type: 'SET_TOPIC', topic });
    resetChatState();
    setShowSubjectSelector(false);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      topic ? p.set('topic', topic) : p.delete('topic');
      p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleTopicClick = useCallback((topic: string) => {
    dispatchSelection({ type: 'SET_TOPIC', topic });
    resetChatState();
    setShowSubjectSelector(false);
  }, [resetChatState]);

  const handleSubtopicChange = useCallback((subtopic: string) => {
    dispatchSelection({ type: 'SET_SUBTOPIC', subtopic });
    resetChatState();
    setShowSubjectSelector(false);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      subtopic ? p.set('subtopic', subtopic) : p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleEditSubject = useCallback(() => {
    dispatchSelection({ type: 'RESET' });
    resetChatState();
    setShowSubjectSelector(true);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('subject'); p.delete('topic'); p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleEditTopic = useCallback(() => {
    dispatchSelection({ type: 'CLEAR_TOPIC' });
    resetChatState();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('topic'); p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleEditSubtopic = useCallback(() => {
    dispatchSelection({ type: 'CLEAR_SUBTOPIC' });
    resetChatState();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleNewQuery = useCallback((subtopic?: string) => {
    const params = new URLSearchParams();
    if (selectedSubject) params.set('subject', selectedSubject);
    navigate(`/r/student/concept-builder?${params.toString()}`, { replace: true });
    dispatchChat({ type: 'RESET' });
    setUserInput('');
    setAwaitingDifficultySelection(false);
    setShowSubjectSelector(false);
    fetchConversationList();
  }, [selectedSubject, navigate, fetchConversationList]);

  const handleNewChatClick = useCallback((topic: string) => {
    dispatchSelection({ type: 'SET_TOPIC', topic });
    resetChatState();
    setShowSubjectSelector(false);
  }, [resetChatState]);

  const handleSidebarTabChange = useCallback((value: number) => {
    setSidebarTabValue(value);
    dispatchSelection({ type: 'SET_EXPANDED_TOPIC', topic: null });
    dispatchChat({ type: 'SET_ACTIVE_CONVERSATION_ID', id: null });
  }, []);

  const handleConceptQuestionClick = useCallback((question: string) => {
    setUserInput(question);
    setShowSubjectSelector(false);
    setTimeout(() => handleApiCall('doubt', undefined, true), 0);
  }, [handleApiCall]);

  const handleSendQuestion = useCallback(() => {
    stopMic();
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    handleApiCall('doubt');
  }, [stopMic, handleApiCall]);

  const handleDifficultySelection = useCallback((difficulty: 'easy' | 'medium' | 'hard') => {
    stopMic();
    setAwaitingDifficultySelection(false);
    handleApiCall('test', difficulty);
  }, [stopMic, handleApiCall]);

  const handleGetTestPaper = useCallback(() => {
    addMessage('user', 'Practice Test');
    addMessage('ai', 'Please select the difficulty level for your test:', { difficulty_buttons: ['easy', 'medium', 'hard'] } as any);
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    setAwaitingDifficultySelection(true);
  }, [addMessage]);

  const handleGetImage = useCallback(async () => {
    if (!lastQuestionLocal.trim() && !queryHistory.trim()) {
      showSnackBar('Please ask a question first before requesting an image.', 'warning');
      return;
    }
    addMessage('ai', `🎨 Generating your image...\nThis may take a moment. Please wait.`);
    try {
      const instructionParts = [
        `You are teaching a student of Class ${studentClass}.`,
        `Answer strictly according to this class level.`,
        ...(selectedSubject ? [`Subject: ${selectedSubject}.`] : []),
        ...(selectedTopic ? [`Topic: ${selectedTopic}.`] : []),
        ...(selectedSubtopic ? [`Subtopic: ${selectedSubtopic}.`] : []),
        activeInstructionSet,
      ];
      const payload = {
        query: lastQuestionLocal || queryHistory,
        instruction: instructionParts.join('\n'),
        flowType: 'doubt',
        campaign_id: campaignId,
        lead_form_id: leadFormId,
        parameters: { subject: selectedSubject, topic: selectedTopic, subtopic: selectedSubtopic, board: selectedBoard },
        class: studentClass,
        ...(conversationId && { conversation_id: conversationId }),
      };
      const response = await actions.getImageUrl(payload);
      if (!response?.status) {
        dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
        addMessage('ai', '❌ Failed to generate image. Please try again.');
      }
    } catch {
      dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
      addMessage('ai', '❌ Failed to generate image. Please try again.');
      showSnackBar('Failed to generate image. Please try again.', 'error');
    }
  }, [lastQuestionLocal, studentClass, queryHistory, selectedSubject, selectedTopic, selectedSubtopic, activeInstructionSet, campaignId, leadFormId, conversationId, addMessage, showSnackBar]);

  const handleNavigateToResponse = useCallback(async () => {
    if (!pendingResponseConversationId) return;
    setShowResponseNotification(false);
    await fetchConversationList();
    await fetchQuery(pendingResponseConversationId);
    if (isMobile) setSidebarOpen(false);
  }, [pendingResponseConversationId, fetchConversationList, fetchQuery, isMobile]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    let text = e.clipboardData.getData('text/html');
    if (text) {
      const div = document.createElement('div');
      div.innerHTML = text;
      text = div.textContent || div.innerText || '';
    } else {
      text = e.clipboardData.getData('text/plain');
    }
    if (!text) return;
    const textarea = textFieldRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const next = userInput.substring(0, start) + text + userInput.substring(end);
      setUserInput(next);
      requestAnimationFrame(() => {
        if (!textFieldRef.current) return;
        const pos = start + text.length;
        textFieldRef.current.selectionStart = pos;
        textFieldRef.current.selectionEnd = pos;
        textFieldRef.current.focus();
      });
    } else {
      setUserInput((p) => p + text);
    }
  }, [userInput]);

  const handleInsertSymbol = useCallback((symbol: string) => {
    setUserInput((p) => p + symbol);
  }, []);

  const handlePredefinedQuestionClick = useCallback((questionData: any, type: 'doubt' | 'test') => {
    const questionText = questionData?.entity?.question || questionData?.question || questionData;
    if (type === 'doubt') {
      addMessage('user', questionText);
      const entity = questionData?.entity;
      if (entity) {
        let aiResponse = '';
        if (entity.definition) aiResponse += `**Definition:**\n${entity.definition}\n\n`;
        if (entity.key_theorem) aiResponse += `**Key Concept:**\n${entity.key_theorem.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n')}\n\n`;
        if (entity.correct_explanation) aiResponse += `**Explanation:**\n${entity.correct_explanation}\n\n`;
        if (entity.real_life_examples) aiResponse += `**Real Life Examples:**\n${entity.real_life_examples}\n\n`;
        if (entity.common_misunderstanding) aiResponse += `**Common Misunderstandings:**\n${entity.common_misunderstanding.replace(/\\n•/g, '\n• ').replace(/\\n-/g, '\n- ')}\n\n`;
        if (entity.sources) aiResponse += `**Sources:**\n${entity.sources.replace(/🔹/g, '• ').replace(/\\n-/g, '\n- ').replace(/- https/g, '• https')}`;
        let conceptQuestions: string[] = [];
        if (typeof entity.concept_check_questions === 'string') {
          conceptQuestions = entity.concept_check_questions.split(/[,\n]/).map((q: string) => q.trim()).filter(Boolean);
        } else if (Array.isArray(entity.concept_check_questions)) {
          conceptQuestions = entity.concept_check_questions;
        }
        dispatchChat({
          type: 'ADD_MESSAGE',
          message: makeMessage('ai', aiResponse, conceptQuestions.length > 0 ? { concept_questions: conceptQuestions } : {}),
        });
        dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });
      } else {
        setUserInput(questionText);
        setShowSubjectSelector(false);
        setTimeout(() => handleApiCall('doubt', undefined, true), 100);
      }
    } else {
      addMessage('user', questionText);
      addMessage('ai', 'Please select the difficulty level for your test:');
      dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    }
  }, [addMessage, handleApiCall]);

  // ─── Sidebar props ──────────────────────────────────────────────────────
  const sidebarProps = useMemo(() => ({
    isMobile,
    selectedSubject,
    selectedTopic,
    selectedSubtopic,
    subjectOptions,
    topicOptions,
    subtopicOptions,
    availableTopics,
    sortedTopics,
    conversations,
    activeConversationId,
    expandedTopic,
    onClose: () => setSidebarOpen(false),
    onSubjectChange: handleSubjectChange,
    onTopicChange: handleTopicChange,
    onSubtopicChange: handleSubtopicChange,
    onTopicClick: handleTopicClick,
    onExpandTopic: (t: string | null) => dispatchSelection({ type: 'SET_EXPANDED_TOPIC', topic: t }),
    onNewChatClick: handleNewChatClick,
    onConversationClick: fetchQuery,
    onTabChange: handleSidebarTabChange,
    isSuperuser: hasClassListPermission,
    availableClasses,
    superuserSelectedClass,
    onSuperuserClassChange: (cls: string) => {
      setSuperuserSelectedClass(cls);
      setStudentClass(cls);
      dispatchSelection({ type: 'RESET' });
      resetChatState();
      setShowSubjectSelector(true);
    },
    selectedOrganisation,
    onOrganisationChange: (org: any) => {
      setSelectedOrganisation(org);
      setSuperuserSelectedClass('');
      setStudentClass('');
      dispatchSelection({ type: 'RESET' });
      resetChatState();
      setShowSubjectSelector(true);
    },
    boardOptions,
    selectedBoard,
    onBoardChange: (board: string) => {
      setSelectedBoard(board);
      setSuperuserSelectedClass('');
      dispatchSelection({ type: 'RESET' });
      resetChatState();
      setShowSubjectSelector(true);
    },
  }), [isMobile, selectedSubject, selectedTopic, selectedSubtopic, subjectOptions, topicOptions, subtopicOptions, availableTopics, sortedTopics, conversations, activeConversationId, expandedTopic, handleSubjectChange, handleTopicChange, handleSubtopicChange, handleTopicClick, handleNewChatClick, fetchQuery, handleSidebarTabChange, isSuperuser, availableClasses, superuserSelectedClass,
    selectedOrganisation, selectedBoard, boardOptions,
    resetChatState]);

  // ─── Card selection handlers ──────────────────────────────────────────────
  const handleBoardSelectCard = useCallback((board: string) => {
    setSelectedBoard(board);
    setSuperuserSelectedClass('');
    setStudentClass('');
    dispatchSelection({ type: 'RESET' });
    resetChatState();
    setShowSubjectSelector(true);
  }, [resetChatState]);

  const handleClassSelectCard = useCallback((cls: string) => {
    setSuperuserSelectedClass(cls);
    setStudentClass(cls);
    dispatchSelection({ type: 'RESET' });
    resetChatState();
    setShowSubjectSelector(true);
  }, [resetChatState]);

  const handleEditBoardCard = useCallback(() => {
    setSelectedBoard('');
    setSuperuserSelectedClass('');
    setStudentClass('');
    dispatchSelection({ type: 'RESET' });
    resetChatState();
    setShowSubjectSelector(true);
  }, [resetChatState]);

  const handleEditClassCard = useCallback(() => {
    setSuperuserSelectedClass('');
    setStudentClass('');
    dispatchSelection({ type: 'RESET' });
    resetChatState();
    setShowSubjectSelector(true);
  }, [resetChatState]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Mobile: Show selector overlay when no subject is selected
  if (isMobile && !selectedSubject && !messages.length) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f0f2f5',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            px: 1,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              position: 'relative',
              minHeight: 0,
            }}
          >
            {/* Header */}
            <Box sx={{
              p: { xs: 0.8, sm: 1.3 },
              bgcolor: 'primary.main',
              color: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
              minHeight: { xs: 48, sm: 56 },
              zIndex: 10,
              position: 'relative',
              width: '100%',
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { md: 1.5, xs: 0.5 },
                paddingLeft: isMobile ? '0' : '0',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(true)} sx={{ color: '#fff', mr: 0.5, flexShrink: 0, p: 0.5 }}>
                    <MenuIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 28, md: 40 }, height: { xs: 28, md: 40 }, flexShrink: 0 }}>
                  <SmartToyIcon sx={{ fontSize: { xs: 16, md: 24 } }} />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', md: '1.25rem' }, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      Concept Builder
                    </Typography>
                    {studentClass && (
                      <Typography sx={{ fontSize: { xs: 8, md: 11 }, color: '#fff', px: 0.5, py: 0.1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)', whiteSpace: 'nowrap' }}>
                        {studentClass}
                      </Typography>
                    )}
                  </Box>
                  {selectedSubject && (
                    <Typography sx={{ fontSize: { xs: 8, md: 12 }, opacity: 0.9, display: 'block', mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 100, sm: 200, md: 300 } }}>
                      {selectedSubject}
                      {selectedTopic && <><span>→</span><span>{selectedTopic}</span></>}
                      {selectedSubtopic && <><span>→</span><span>{selectedSubtopic}</span></>}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.25, sm: 0.5 },
                flexShrink: 0,
                ml: 0.5,
              }}>
                <IconButton
                  sx={{ color: '#fff', p: { xs: 0.5, sm: 1 } }}
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                >
                  <MoreVertIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
                <Menu
                  anchorEl={moreMenuAnchor}
                  open={Boolean(moreMenuAnchor)}
                  onClose={() => setMoreMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: { minWidth: 180, borderRadius: 2, mt: 0.5 } }}
                >
                  <MenuItem onClick={() => { navigate('/r/student/create-test'); setMoreMenuAnchor(null); }}>
                    <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                    Practice Test
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/r/student/knowledge-map'); setMoreMenuAnchor(null); }}>
                    <ListItemIcon><AutoStoriesIcon fontSize="small" /></ListItemIcon>
                    Knowledge Map
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/r/student/performance-map'); setMoreMenuAnchor(null); }}>
                    <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
                    Performance Map
                  </MenuItem>
                </Menu>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ConceptBuilderHelp standalone={true} />
                </Box>
              </Box>
            </Box>

            {/* Subject Selection Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                bgcolor: '#f0f2f5',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 0,
              }}
            >
              <TopicSubtopicCards
                selectedSubject={selectedSubject}
                selectedTopic={selectedTopic}
                selectedSubtopic={selectedSubtopic}
                availableSubjects={subjectOptions.map((s) => s.value)}
                availableTopics={availableTopics}
                availableSubtopics={availableSubtopics}
                onSubjectSelect={handleSubjectChange}
                onTopicSelect={(topic) => { dispatchSelection({ type: 'SET_TOPIC', topic }); setShowSubjectSelector(false); }}
                onSubtopicSelect={(subtopic) => { dispatchSelection({ type: 'SET_SUBTOPIC', subtopic }); setShowSubjectSelector(false); }}
                filteredQuestions={filteredQuestionsComputed}
                onPredefinedQuestionClick={handlePredefinedQuestionClick}
                onEditSubject={handleEditSubject}
                onEditTopic={handleEditTopic}
                onEditSubtopic={handleEditSubtopic}
                isSuperuser={hasClassListPermission}
                boardOptions={boardOptions}
                selectedBoard={selectedBoard}
                onBoardSelect={handleBoardSelectCard}
                onEditBoard={handleEditBoardCard}
                availableClasses={availableClasses}
                selectedClass={superuserSelectedClass}
                onClassSelect={handleClassSelectCard}
                onEditClass={handleEditClassCard}
                organisationRequired={loggedInUser?.is_superuser === true}
                organisationSelected={Boolean(selectedOrganisation)}
                selectedOrganisationLabel={
                  selectedOrganisation?.name ||
                  selectedOrganisation?.organisation_name ||
                  selectedOrganisation?.label ||
                  'Organisation'
                }
              />
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // ─── Desktop / Mobile with selection ──────────────────────────────────────
  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f0f2f5',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Container
        maxWidth="xl"
        className="message-outerwrapper"
        sx={{
          py: 0.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 1, md: 'auto' },
          maxWidth: '100% !important',
          height: '100%',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Grid
          container
          spacing={0.25}
          sx={{
            flex: 1,
            overflow: 'hidden',
            height: '100%',
            minHeight: 0,
          }}
        >
          {isMobile && (
            <Drawer
              anchor="left"
              open={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{ '& .MuiDrawer-paper': { width: '85%', maxWidth: 360 } }}
            >
              <ConceptBuilderSidebar {...sidebarProps} />
            </Drawer>
          )}
          {!isMobile && (
            <Grid item md={3} sx={{ height: '100%', minHeight: 0 }}>
              <Paper elevation={2} sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <ConceptBuilderSidebar {...sidebarProps} />
              </Paper>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            md={isMobile ? 12 : 9}
            sx={{
              height: '100%',
              minHeight: 0,
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                mb: '5px',
                position: 'relative',
                height: '100%',
                minHeight: 0,
              }}
            >
              {/* Header */}
              <Box sx={{
                p: { xs: 0.8, sm: 1.3 },
                bgcolor: 'primary.main',
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                minHeight: { xs: 48, sm: 56 },
                zIndex: 10,
                position: 'relative',
                width: '100%',
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { md: 1.5, xs: 0.5 },
                  paddingLeft: isMobile ? '8px' : '0',
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                }}>
                  {isMobile && (
                    <IconButton onClick={() => setSidebarOpen(true)} sx={{ color: '#fff', mr: 0.5, flexShrink: 0, p: 0.5 }}>
                      <MenuIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  )}
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 28, md: 40 }, height: { xs: 28, md: 40 }, flexShrink: 0 }}>
                    <SmartToyIcon sx={{ fontSize: { xs: 16, md: 24 } }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', md: '1.25rem' }, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                        Concept Builder
                      </Typography>
                      {studentClass && (
                        <Typography sx={{ fontSize: { xs: 8, md: 11 }, color: '#fff', px: 0.5, py: 0.1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)', whiteSpace: 'nowrap' }}>
                          {studentClass}
                        </Typography>
                      )}
                    </Box>
                    {selectedSubject && (
                      <Typography sx={{ fontSize: { xs: 8, md: 12 }, opacity: 0.9, display: 'block', mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 100, sm: 200, md: 300 } }}>
                        {selectedSubject}
                        {selectedTopic && <><span>→</span><span>{selectedTopic}</span></>}
                        {selectedSubtopic && <><span>→</span><span>{selectedSubtopic}</span></>}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.25, sm: 0.5 },
                  flexShrink: 0,
                }}>
                  <IconButton
                    sx={{ color: '#fff', p: { xs: 0.5, sm: 1 } }}
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  >
                    <MoreVertIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </IconButton>
                  <Menu
                    anchorEl={moreMenuAnchor}
                    open={Boolean(moreMenuAnchor)}
                    onClose={() => setMoreMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { minWidth: 180, borderRadius: 2, mt: 0.5 } }}
                  >
                    <MenuItem onClick={() => { navigate('/r/student/create-test'); setMoreMenuAnchor(null); }}>
                      <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                      Practice Test
                    </MenuItem>
                    <MenuItem onClick={() => { navigate('/r/student/knowledge-map'); setMoreMenuAnchor(null); }}>
                      <ListItemIcon><AutoStoriesIcon fontSize="small" /></ListItemIcon>
                      Knowledge Map
                    </MenuItem>
                    <MenuItem onClick={() => { navigate('/r/student/performance-map'); setMoreMenuAnchor(null); }}>
                      <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
                      Performance Map
                    </MenuItem>
                  </Menu>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ConceptBuilderHelp standalone={true} />
                  </Box>
                </Box>
              </Box>

              {/* Chat area - Takes remaining space */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: { xs: 2, md: 3 },
                  bgcolor: '#f0f2f5',
                  backgroundImage: `radial-gradient(circle at 20px 20px, rgba(0,0,0,0.02) 1px, transparent 1px), radial-gradient(circle at 80px 80px, rgba(0,0,0,0.02) 1px, transparent 1px)`,
                  backgroundSize: '100px 100px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: messages.length === 0 ? 'center' : 'flex-start',
                  alignItems: messages.length === 0 ? 'center' : 'stretch',
                  minHeight: 0,
                }}
              >
                {messages.length === 0 ? (
                  <TopicSubtopicCards
                    selectedSubject={selectedSubject}
                    selectedTopic={selectedTopic}
                    selectedSubtopic={selectedSubtopic}
                    availableSubjects={subjectOptions.map((s) => s.value)}
                    availableTopics={availableTopics}
                    availableSubtopics={availableSubtopics}
                    onSubjectSelect={handleSubjectChange}
                    onTopicSelect={(topic) => { dispatchSelection({ type: 'SET_TOPIC', topic }); setShowSubjectSelector(false); }}
                    onSubtopicSelect={(subtopic) => { dispatchSelection({ type: 'SET_SUBTOPIC', subtopic }); setShowSubjectSelector(false); }}
                    filteredQuestions={filteredQuestionsComputed}
                    onPredefinedQuestionClick={handlePredefinedQuestionClick}
                    onEditSubject={handleEditSubject}
                    onEditTopic={handleEditTopic}
                    onEditSubtopic={handleEditSubtopic}
                    isSuperuser={hasClassListPermission}
                    boardOptions={boardOptions}
                    selectedBoard={selectedBoard}
                    onBoardSelect={handleBoardSelectCard}
                    onEditBoard={handleEditBoardCard}
                    availableClasses={availableClasses}
                    selectedClass={superuserSelectedClass}
                    onClassSelect={handleClassSelectCard}
                    onEditClass={handleEditClassCard}
                    organisationRequired={loggedInUser?.is_superuser === true}
                    organisationSelected={Boolean(selectedOrganisation)}
                    selectedOrganisationLabel={
                      selectedOrganisation?.name ||
                      selectedOrganisation?.organisation_name ||
                      selectedOrganisation?.label ||
                      'Organisation'
                    }
                  />
                ) : (
                  <>
                    {messages.map((msg, index) => {
                      const showDateSep =
                        index === 0 ||
                        (msg.created_at && messages[index - 1]?.created_at && formatDate(msg.created_at) !== formatDate(messages[index - 1].created_at));
                      return (
                        <React.Fragment key={msg.id}>
                          {showDateSep && msg.created_at && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                              <Paper elevation={0} sx={{ px: 2, py: 0.5, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #bbdefb' }}>
                                <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: '#1976d2' }}>
                                  {formatDate(msg.created_at)}
                                </Typography>
                              </Paper>
                            </Box>
                          )}
                          <CommonChatBubble
                            message={msg}
                            aiAvatar={
                              <Box sx={{
                                width: 32, height: 32, borderRadius: '10px',
                                bgcolor: 'primary.main', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }}>
                                <SmartToyIcon sx={{ color: '#fff', fontSize: 16 }} />
                              </Box>
                            }
                            renderAiContent={(c) => <MathRenderer content={c} />}
                            diagramSlot={msg.diagram_mermaid
                              ? <MermaidDiagram code={msg.diagram_mermaid} title={msg.diagram_title} />
                              : undefined}
                            videoSlot={msg.video_scenes?.length
                              ? <AnimatedVideoPlayer scenes={msg.video_scenes!} title={msg.video_title} />
                              : undefined}
                            imageSlot={
                              <>
                                {getValidImageUrls(msg.image_url).map((url, i) => (
                                  <ImagePreview key={`${msg.id}-${i}`} src={url} alt="Content image" title="Image" index={i} />
                                ))}
                              </>
                            }
                            onConceptClick={handleConceptQuestionClick}
                            onDifficultySelect={handleDifficultySelection}
                            formatTime={(d) => formatTime(d, true)}
                            variant="default"
                          />
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </Box>

              <FloatingActionBar
                show={(showActionButtons || !isWaitingForResponse) && messages.length > 0}
                awaitingDifficultySelection={awaitingDifficultySelection}
                onNewQuery={handleNewQuery}
                onGetTestPaper={handleGetTestPaper}
                handleGetImage={handleGetImage}
              />

              {/* Input area - Fixed at bottom */}
              <Box sx={{
                p: 0,
                borderTop: '1px solid #e5e7eb',
                bgcolor: '#fff',
                flexShrink: 0,
                position: 'relative',
                zIndex: 5,
              }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />

                {/* Image preview strip */}
                {imageFiles.length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e5e7eb',
                    overflowX: 'auto',
                    flexWrap: 'nowrap',
                  }}>
                    {imageFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          p: 0.75,
                          borderRadius: 1.5,
                          bgcolor: '#fff',
                          border: '1px solid #e5e7eb',
                          flexShrink: 0,
                          position: 'relative',
                          '&:hover': {
                            borderColor: '#667eea',
                            boxShadow: '0 2px 12px rgba(102,126,234,0.15)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <img
                          src={imagePreviewUrls[index]}
                          alt="preview"
                          style={{
                            height: 44,
                            width: 44,
                            objectFit: 'cover',
                            borderRadius: 6,
                          }}
                        />
                        <Typography sx={{
                          fontSize: 11,
                          color: '#555',
                          maxWidth: 80,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(index)}
                          sx={{
                            p: 0.25,
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            '&:hover': {
                              bgcolor: '#fef2f2',
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {imageFiles.length > 1 && (
                      <Chip
                        label={`${imageFiles.length} images`}
                        size="small"
                        onDelete={handleClearImage}
                        sx={{
                          fontSize: 10,
                          height: 28,
                          bgcolor: '#667eea',
                          color: '#fff',
                          '& .MuiChip-deleteIcon': {
                            color: '#fff',
                            fontSize: 14,
                          },
                        }}
                      />
                    )}
                  </Box>
                )}

                {/* MAIN INPUT ROW */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 1.5,
                  bgcolor: '#fff',
                }}>
                  {/* TEXT INPUT WITH PLUS INSIDE */}
                  <Box sx={{
                    flex: 1,
                    position: 'relative',
                  }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={6}
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onPaste={handlePaste}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendQuestion();
                        }
                      }}
                      placeholder={
                        isListening
                          ? 'Listening...'
                          : isSelectionRequired && (!selectedSubject)
                            ? 'Please select Subject first'
                            : 'Ask a question...'
                      }
                      disabled={isSelectionRequired && (!selectedSubject)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          fontSize: { xs: 14, md: 15 },
                          bgcolor: '#f7f8fa',
                          minHeight: 52,
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: '#e5e7eb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#667eea',
                            borderWidth: 2,
                          },
                          '&.Mui-focused': {
                            bgcolor: '#fff',
                            boxShadow: '0 0 0 4px rgba(102,126,234,0.1)',
                          },
                          transition: 'all 0.2s ease',
                        },
                        '& .MuiInputBase-input': {
                          py: 1.5,
                          pl: 4.5,
                          pr: 1.5,
                          lineHeight: 1.6,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={isListening ? 'Stop recording' : 'Voice input'} arrow>
                              <span>
                                <IconButton
                                  onClick={handleMicClick}
                                  disabled={isSelectionRequired && (!selectedSubject)}
                                  size="small"
                                  sx={{
                                    color: isListening ? '#ef4444' : '#667eea',
                                    bgcolor: isListening ? '#fef2f2' : 'transparent',
                                    borderRadius: '50%',
                                    width: 34,
                                    height: 34,
                                    '&:hover': {
                                      bgcolor: isListening ? '#fee2e2' : 'rgba(102,126,234,0.08)',
                                    },
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    '&::after': isListening ? {
                                      content: '""',
                                      position: 'absolute',
                                      inset: -4,
                                      borderRadius: '50%',
                                      border: '2px solid #ef4444',
                                      animation: 'ripple 1.5s ease-in-out infinite',
                                    } : {},
                                  }}
                                >
                                  <MicIcon fontSize="small" />
                                  {isListening && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: -2,
                                        right: -2,
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: '#ef4444',
                                        animation: 'pulse-dot 1.5s ease-in-out infinite',
                                      }}
                                    />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* PLUS BUTTON - INSIDE INPUT BOTTOM-LEFT */}
                    <Box sx={{
                      position: 'absolute',
                      left: 4,
                      bottom: 4,
                      display: 'flex',
                      alignItems: 'center',
                      zIndex: 1,
                    }}>
                      <ActionMenu
                        onAttachFile={() => fileInputRef.current?.click()}
                        onInsertSymbol={handleInsertSymbol}
                        selectedSubject={selectedSubject}
                        instructionMode={instructionMode}
                        onInstructionModeChange={(mode) => setInstructionMode(mode as any)}
                        imageCount={imageFiles.length}
                        disabled={isSelectionRequired && (!selectedSubject)}
                      />
                    </Box>

                    {/* MODE INDICATOR - INSIDE INPUT BOTTOM-RIGHT */}
                    {instructionMode !== 'default' && (
                      <Box sx={{
                        position: 'absolute',
                        right: 8,
                        bottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        zIndex: 1,
                      }}>
                        <Chip
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              {instructionMode === 'solution' && <SchoolIcon sx={{ fontSize: 10 }} />}
                              {instructionMode === 'reasoning' && <LightbulbIcon sx={{ fontSize: 10 }} />}
                              {instructionMode === 'diagram' && <DescriptionIcon sx={{ fontSize: 10 }} />}
                              {instructionMode === 'video' && <VideoLibraryIcon sx={{ fontSize: 10 }} />}
                              <Typography sx={{ fontSize: 8, fontWeight: 500 }}>
                                {instructionMode === 'solution' && 'Solution'}
                                {instructionMode === 'reasoning' && 'Reasoning'}
                                {instructionMode === 'diagram' && 'Diagram'}
                                {instructionMode === 'video' && 'Video'}
                              </Typography>
                            </Box>
                          }
                          size="small"
                          onDelete={() => setInstructionMode('default')}
                          sx={{
                            height: 22,
                            bgcolor: 'rgba(102,126,234,0.08)',
                            border: '1px solid rgba(102,126,234,0.15)',
                            '& .MuiChip-label': { px: 0.5, fontSize: 9 },
                            '& .MuiChip-deleteIcon': { fontSize: 12, color: '#667eea' },
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* SEND BUTTON */}
                  <Tooltip title="Send message" arrow>
                    <IconButton
                      onClick={handleSendQuestion}
                      disabled={!userInput.trim() && imageFiles.length === 0}
                      sx={{
                        alignSelf: 'center',
                        background: (userInput.trim() || imageFiles.length > 0)
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : '#e5e7eb',
                        color: '#fff',
                        width: 33,
                        height: 33,
                        borderRadius: '50%',
                        flexShrink: 0,
                        '&:hover': {
                          background: (userInput.trim() || imageFiles.length > 0)
                            ? 'linear-gradient(135deg, #5a6fd6 0%, #6a3f8f 100%)'
                            : '#e5e7eb',
                          transform: (userInput.trim() || imageFiles.length > 0) ? 'scale(1.05)' : 'none',
                          boxShadow: (userInput.trim() || imageFiles.length > 0)
                            ? '0 8px 24px rgba(102,126,234,0.4)'
                            : 'none',
                        },
                        '&:disabled': {
                          background: '#e5e7eb',
                          color: '#bdbdbd',
                        },
                        transition: 'all 0.2s ease',
                        boxShadow: (userInput.trim() || imageFiles.length > 0)
                          ? '0 4px 16px rgba(102,126,234,0.35)'
                          : 'none',
                      }}
                    >
                      <SendIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* RECORDING INDICATOR */}
                {isListening && (
                  <Box sx={{
                    px: 2,
                    pb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    bgcolor: '#fff',
                  }}>
                    <Chip
                      label="Recording..."
                      size="small"
                      sx={{
                        bgcolor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        height: 28,
                        animation: 'pulse-soft 1.5s ease-in-out infinite',
                        '& .MuiChip-label': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        },
                      }}
                      icon={
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#ef4444',
                            animation: 'pulse-dot 1s ease-in-out infinite',
                          }}
                        />
                      }
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <ResponseNotificationSnackbar
          open={showResponseNotification}
          notificationMessage={notificationMessage}
          onView={handleNavigateToResponse}
          onDismiss={() => { setShowResponseNotification(false); setPendingResponseConversationId(null); }}
        />
      </Container>
    </Box >
  );
};

export default DoubtAssistantPage;